import { useCallback, useEffect, useMemo, useState } from 'react'
import type { DayDoc, DayResult, QuestionResult } from '../lib/types'
import { guessValue, scoreQuestion, formatBig, formatExact, emojiForResult } from '../lib/scoring'
import { getInputMethod, INPUT_METHODS } from './inputs/registry'
import { DigitPad } from './DigitPad'
import { ScaleGauge } from './ScaleGauge'
import { renderWhen } from '../lib/day'
import { MAG_LABELS as MAG_LABELS_SHORT } from '../lib/scoring'
import { Reveal } from './Reveal'
import { submitResults } from '../lib/histogram'
import { saveResult } from '../lib/storage'
import { syncResult } from '../lib/user'
import { fetchLiveNow } from '../lib/liveNow'

export function Play({ day, replay, practice, timed, onZoom, onDone }: { day: DayDoc; replay: boolean; practice?: boolean; timed?: number; onZoom: (z: number) => void; onDone: (r: DayResult) => void }) {
  const Input = (INPUT_METHODS.find((m) => m.id === getInputMethod()) ?? INPUT_METHODS[0]).component
  const [i, setI] = useState(0)
  const [exp, setExp] = useState<number | null>(null)
  const [digit, setDigit] = useState<number | null>(null)
  const [results, setResults] = useState<QuestionResult[]>([])
  const [revealing, setRevealing] = useState<QuestionResult | null>(null)
  const [bump, setBump] = useState(0)
  const [shake, setShake] = useState(false)
  const [liveNow, setLiveNow] = useState<number | null>(null)

  const q = day.questions[i]
  const [clock, setClock] = useState<number | null>(null)

  // bonus mode: a per-question countdown; zero = timeout, no points, ⏰
  useEffect(() => {
    if (!timed || revealing) { setClock(null); return }
    setClock(timed)
    const t0 = Date.now()
    const iv = setInterval(() => {
      const left = timed - Math.floor((Date.now() - t0) / 1000)
      setClock(Math.max(0, left))
      if (left <= 0) {
        clearInterval(iv)
        setRevealing({ id: q.id, guess: 0, answer: q.answer, ratio: 0, points: 0, qscore: 0, timedOut: true })
      }
    }, 250)
    return () => clearInterval(iv)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i, timed, revealing == null])
  const onExp = useCallback((e: number) => { setExp(e); setBump((b) => b + 1) }, [])
  const value = exp != null && digit != null ? guessValue({ exp, digit }) : null

  // Pre-fetch "the number moved" for live questions while the player is still guessing.
  useEffect(() => {
    setLiveNow(null)
    if (q.liveSource && !day.local) fetchLiveNow(q.liveSource).then(setLiveNow).catch(() => {})
  }, [q, day.local])

  const lock = () => {
    if (value == null) return
    const r = scoreQuestion(q.id, value, q.answer)
    setRevealing(r)
    if (r.points === 0) { setTimeout(() => setShake(true), 1300); setTimeout(() => setShake(false), 1900) }
  }

  const next = () => {
    const all = [...results, revealing!]
    setResults(all); setRevealing(null); setExp(null); setDigit(null)
    if (i + 1 < day.questions.length) { setI(i + 1); return }
    const score = all.reduce((a, r) => a + r.qscore, 0)
    const dr: DayResult = {
      date: day.date, number: day.number, theme: day.theme, score,
      emoji: all.map(emojiForResult).join(''), results: all, playedAt: new Date().toISOString(), replay,
    }
    if (!practice) {
      saveResult(dr)
      syncResult(dr)
      submitResults(day.date, all, score).catch((e) => console.warn('submit failed', e))
    }
    onDone(dr)
  }

  const isLive = q.tier === 'live' || q.tier === 'yesterday'
  const progress = useMemo(() => day.questions.map((_, k) => k < i ? 'done' : k === i ? 'cur' : ''), [day, i])

  if (revealing) {
    return (
      <div className={'screen reveal-screen' + (shake ? ' shake' : '')}>
        <div className="eyebrow big"><span>{day.theme.emoji} {day.theme.name}</span><span style={{ marginLeft: 'auto' }}>{i + 1} / {day.questions.length}</span></div>
        <p className="reveal-q">{renderWhen(q)}</p>
        <Reveal key={q.id} q={q} r={revealing} onZoom={onZoom} onNext={next} isLast={i + 1 === day.questions.length} liveNow={liveNow} qIndex={i} qTotal={day.questions.length} />
      </div>
    )
  }

  return (
    <div className="screen wide">
      <div className="game-grid">
      <div className="game-info">
      <div className="eyebrow"><span>{day.theme.emoji} {day.theme.name}</span>{day.number > 0 && <span className="n">#{day.number}</span>}{clock != null && <span className={'clock' + (clock <= 5 ? ' low' : '')}>⏱ {clock}s</span>}<span style={{ marginLeft: 'auto' }}>{i + 1} / {day.questions.length}</span></div>
      <div className="progress">{progress.map((c, k) => <i key={k} className={c} />)}</div>
      <h2 className="question">{renderWhen(q)}{isLive && <span className="live">FROZEN FACT</span>}{q.hero && <span className="live">⭐ FINAL BOSS</span>}</h2>

      <div className="hero slim">
        {value != null ? (
          <div key={bump} className="bump"><span className="exact-line">{formatExact(value)} {q.unit}</span></div>
        ) : (
          <span className="exact-line dim">{exp != null ? 'now pick a digit 1–9' : 'pick a magnitude, then a digit'}</span>
        )}
      </div>

      </div>
      <div className="game-input">
      <ScaleGauge exp={exp} compact />
      <Input value={exp} onChange={onExp}
        label={exp == null ? '' : value != null ? formatBig(value).replace(/(million|billion|trillion|quadrillion|quintillion)/, (m) => ({million:'M',billion:'B',trillion:'T',quadrillion:'Qa',quintillion:'Qi'} as any)[m]).replace(' ', '') : MAG_LABELS_SHORT[exp]}
        sublabel={value != null ? q.unit : 'pick a digit'} />
      <DigitPad digit={digit} onDigit={setDigit} canLock={value != null} onLock={lock} />
      </div>
      </div>
    </div>
  )
}
